import { ChangeDetectionStrategy, Component, computed, inject, Input, OnInit, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NgClass } from '@angular/common';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { EditPlayerCommandHandler } from '../../application/handlers';
import { map, take } from 'rxjs';
import { GamePlayerModel, MODAL_TOKEN, ModalProvider } from '@core';
import { HostedGamesService } from '@hosted-games/infra';
import { IonicModule } from '@ionic/angular';
import { AddPlayerCommandHandler } from '../../application/handlers';

@Component({
  selector: 'lib-add-custom-player',
  templateUrl: './add-custom-player.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule, NgClass, IonicModule],
  providers: [AddPlayerCommandHandler, HostedGamesService, EditPlayerCommandHandler]
})
export class AddCustomPlayerComponent implements OnInit {
  @Input() gameId!: string;
  @Input() player!: GamePlayerModel;
  @Input() isEdit: boolean = false;

  private readonly addPlayerCommandHandler: AddPlayerCommandHandler = inject(AddPlayerCommandHandler);
  private readonly editPlayerCommandHandler: EditPlayerCommandHandler = inject(EditPlayerCommandHandler);
  private readonly modalProvider: ModalProvider = inject(MODAL_TOKEN);

  readonly addPLayerForm: FormGroup = new FormGroup({
    displayName: new FormControl('', [Validators.required])
  });

  private readonly displayName: Signal<string | undefined> = toSignal(
    this.addPLayerForm.valueChanges.pipe(map((form: { displayName: string }) => form.displayName))
  );

  readonly canSubmit: Signal<boolean> = computed(() => {
    const displayName: string = (this.displayName() ?? '').trim();

    if (!displayName) return false;

    return !this.isEdit || displayName !== this.player?.userDisplayName;
  });

  ngOnInit(): void {
    this.prepopulateForm();
  }

  handleAction(): void {
    if (this.isEdit) {
      this.editPlayer();
    } else {
      this.addPlayer();
    }
  }

  closeModal(): void {
    this.modalProvider.dismissAllStoredModals();
  }

  private prepopulateForm(): void {
    if (this.isEdit) {
      this.addPLayerForm.patchValue({
        displayName: this.player.userDisplayName
      });
    }
  }

  private addPlayer(): void {
    const displayName: string = this.displayName() ?? '';
    this.addPlayerCommandHandler
      .addPlayer(this.gameId, displayName)
      .pipe(take(1))
      .subscribe(() => this.closeModal());
  }

  private editPlayer(): void {
    const displayName: string = this.displayName() ?? '';
    this.editPlayerCommandHandler
      .editPlayer(this.gameId, this.player.playerId, displayName)
      .pipe(take(1))
      .subscribe(() => this.closeModal());
  }
}
